import React from 'react';
import { Alert, Button, Chip, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RedoIcon from '@mui/icons-material/Redo';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import {
  ISimulationActions, ISimulationReadiness, ISimulationSubmission,
} from './simulation.models';

type Props = {
  readiness: ISimulationReadiness;
  submission: ISimulationSubmission;
  actions: ISimulationActions;
  loading?: boolean;
  onSubmit: (notes: string) => void;
  onResubmit: (notes: string) => void;
  onAcknowledgeReturn: () => void;
  onOpenExecutiveReview: () => void;
};
type State = { notes: string };

class SimulationReviewPanel extends React.Component<Props, State> {
  state: State = { notes: '' };

  private stateMessage() {
    const { readiness, submission } = this.props;
    if (submission.recommended) return 'Executive Review has recommended this scenario. Continue from Executive Review to Determinant Budget.';
    if (submission.returnFeedback) return submission.returnFeedback.reason || 'Executive Review returned the simulation for revision.';
    if (submission.submitted) return 'The frozen simulation package has been submitted to Executive Review.';
    return readiness.message || 'Resolve all blocking controls before submission to Executive Review.';
  }

  render() {
    const { readiness, submission, actions } = this.props;
    const returned = Boolean(submission.returnFeedback);
    return (
      <Stack spacing={2}>
        <Alert severity={submission.recommended ? 'success' : returned ? 'warning' : submission.submitted ? 'info' : readiness.ready ? 'info' : 'warning'}>
          {this.stateMessage()}
        </Alert>

        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Executive Review Readiness</Typography>
            <Chip size="small" label={`Score ${readiness.score ?? 0}%`} color={readiness.ready ? 'success' : 'warning'} />
          </Stack>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {[
              ['Forecast baseline', readiness.forecastready],
              ['Assumptions', readiness.assumptionsready],
              ['Formula computation', readiness.formulasready],
              ['Constraints', readiness.constraintsready],
              ['Economic capacity', readiness.capacityready],
              ['Sensitivity evidence', readiness.sensitivityready !== false],
            ].map(([label, ready]) => (
              <Grid size={{ xs: 12, md: 4 }} key={String(label)}>
                <Alert severity={ready ? 'success' : 'warning'}>{label}</Alert>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {submission.submitted && (
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Frozen Submission</Typography>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid size={{ xs: 12, md: 3 }}><Typography variant="caption">Version</Typography><Typography>{submission.submissionversion || 1}</Typography></Grid>
              <Grid size={{ xs: 12, md: 3 }}><Typography variant="caption">Submitted</Typography><Typography>{submission.submittedat || '—'}</Typography></Grid>
              <Grid size={{ xs: 12, md: 3 }}><Typography variant="caption">Frozen computation</Typography><Typography>{submission.frozencomputationix || '—'}</Typography></Grid>
              <Grid size={{ xs: 12, md: 3 }}><Typography variant="caption">Executive state</Typography><Typography>{submission.executiveReviewState || 'SUBMITTED'}</Typography></Grid>
            </Grid>
          </Paper>
        )}

        {returned && (
          <Alert icon={<AssignmentReturnIcon />} severity="warning">
            <Typography sx={{ fontWeight: 700 }}>{submission.returnFeedback?.returncategoryname || 'Returned for revision'}</Typography>
            <Typography variant="body2">{submission.returnFeedback?.reason}</Typography>
          </Alert>
        )}

        <TextField label={returned ? 'Resubmission Notes' : 'Submission Notes'} value={this.state.notes}
          onChange={(e) => this.setState({ notes: e.target.value })} multiline minRows={3} fullWidth
          disabled={submission.submitted && !returned} />

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="contained" startIcon={<SendIcon />} onClick={() => this.props.onSubmit(this.state.notes)}
            disabled={!actions.canSubmitExecutiveReview || this.props.loading}>Submit to Executive Review</Button>
          <Button variant="contained" color="warning" startIcon={<RedoIcon />} onClick={() => this.props.onResubmit(this.state.notes)}
            disabled={!actions.canResubmitExecutiveReview || this.props.loading}>Resubmit to Executive Review</Button>
          <Button variant="outlined" startIcon={<AssignmentReturnIcon />} onClick={this.props.onAcknowledgeReturn}
            disabled={!actions.canAcknowledgeReturn || this.props.loading}>Acknowledge Return</Button>
          <Button variant="outlined" startIcon={<RateReviewIcon />} onClick={this.props.onOpenExecutiveReview}
            disabled={!actions.canOpenExecutiveReview || this.props.loading}>Open Executive Review</Button>
        </Stack>
      </Stack>
    );
  }
}
export default SimulationReviewPanel;
